import Vue from 'vue'
import _ from 'lodash'
import PathMixin from '~mixins/PathMixin'
import CONFIG from '~config/env'
import FineUploaderS3 from 'fine-uploader-wrappers/s3'
import { v4 as uuid } from 'uuid'
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png']

export default Vue.extend({
  mixins: [PathMixin],
  props: {
    private: {
      type: Boolean,
      default: true
    },
    context: {
      type: Object,
      default: () => {
        // Default context is empty object
      }
    },
    autoStart: {
      type: Boolean,
      default: true
    },
    basePath: {
      type: String,
      default: 'files/{orgId}/{fileName}'
    },
    imageUpload: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: true
    },
    required: {
      type: Boolean,
      default: false
    },
    allowedExtensions: {
      type: Array,
      default: () => []
    },
    sizeLimit: {
      type: Number,
      default: 1024 * 1024 * 1024 * 1024 * 1 // 1GB
    },
    itemLimit: {
      type: Number,
      default: 0
    },
    validateRepeatedFiles: {
      type: Boolean,
      default: true
    },
    silent: {
      type: Boolean,
      default: false
    },
    customOptions: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'complete',
    'upload-complete',
    'all-complete',
    'error',
    'status-change',
    'cancel',
    'cancel-all',
    'total-progress',
    'progress',
    'upload-chunk',
    'files-selected',
    'all-upload-complete'
  ],
  data() {
    return {
      fineUploader: {},
      started: false,
      totalSize: 0,
      numFiles: 0,
      numSentFiles: 0,
      bytesUploaded: 0,
      bytesUploadedOverTime: [],
      mbps: 0,
      estimatedTime: 0,
      paused: false,
      fileUuids: {}
    }
  },
  computed: {
    uploadProgress() {
      if (this.totalSize === 0 || this.bytesUploaded === 0) {
        return 0
      }

      return (this.bytesUploaded / this.totalSize) * 100
    },
    chunkingEnabled() {
      return this.fineUploader.options.chunking && this.fineUploader.options.chunking.enabled
    },
    deleteEnabled() {
      return this.fineUploader.options.deleteFile && this.fineUploader.options.deleteFile.enabled
    },
    qq() {
      return this.fineUploader.qq
    },
    thread() {
      return 'upload_' + uuid()
    },
    accept() {
      if (!this.allowedExtensions.length) {
        return this.imageUpload ? 'image/*' : ''
      }

      return '.' + _.join(this.allowedExtensions, ', .')
    },
    awsS3Url() {
      return this.private ? CONFIG.AWS.S3_PRIVATE_URL : CONFIG.AWS.S3_PUBLIC_URL
    },
    awsS3Host() {
      return this.private ? CONFIG.AWS.S3_PRIVATE_HOST : CONFIG.AWS.S3_PUBLIC_HOST
    },
    defaultOptions() {
      return {
        autoUpload: false,
        messages: {
          onLeave: this.$t('on_leave_error'),
          sizeError: this.$t('file_size_error').replace(/\[/g, '{').replace(/\]/g, '}'),
          typeError: this.$t('invalid_extension_error').replace(/\[/g, '{').replace(/\]/g, '}'),
          tooManyItemsError: this.$t('too_many_items_error').replace(/\[/g, '{').replace(/\]/g, '}')
        },
        text: {
          defaultError: this.$t('default_error'),
          waitingForResponse: this.$t('in_processing') + '...',
          failUpload: this.$t('upload_has_failed'),
          paused: this.$t('paused')
        },
        request: {
          endpoint: this.awsS3Url,
          accessKey: CONFIG.AWS.ACCESS_KEY,
          params: {
            'Content-Disposition': 'attachment',
            origin: window.location.origin
          },
          customHeaders: {
            origin: window.location.origin
          }
        },
        signature: {
          endpoint: CONFIG.AWS.SINGATURE_URL() + CONFIG.API.ENDPOINTS.SIGNED_POST,
          version: 4,
          customHeaders: {
            authorization: 'Bearer ' + this.$authToken
          }
        },
        objectProperties: {
          host: this.awsS3Host,
          region: CONFIG.AWS.S3_REGION,
          key: fileId => {
            const fileName = this.fineUploader.methods.getName(fileId)
            if (this.fineUploader.getKeyNameFunction) {
              return this.fineUploader.getKeyNameFunction(this.fineUploader, fileId, fileName)
            } else {
              return this.keyNameFunction(fileId, fileName)
            }
          }
        },
        validation: {
          sizeLimit: 106300440576,
          stopOnFirstInvalidFile: false
        },
        cors: {
          expected: true
        },
        retry: {
          enableAuto: true,
          maxAutoAttempts: 1000
        },
        chunking: {
          enabled: true,
          mandatory: true
        },
        resume: {
          enabled: true
        },
        paste: {
          targetElement: document,
          promptForName: true
        },
        callbacks: {
          onAllComplete: this.onAllComplete,
          onError: this.onError,
          onValidateBatch: this.onValidateBatch,
          onStatusChange: this.onStatusChange,
          onCancel: this.onCancel
        }
      }
    }
  },
  watch: {
    sizeLimit(val) {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.options.validation.sizeLimit = val
      }
    },
    allowedExtensions(val) {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.options.validation.allowedExtensions = val
      }
    },
    itemLimit(val) {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.options.validation.itemLimit = val
      }
    },
    validateRepeatedFiles(val) {
      if (val === false) {
        this.fineUploader.off('validate', this.onValidateHandler)
      } else {
        this.fineUploader.on('validate', this.onValidateHandler)
      }
    }
  },
  created() {
    this.fineUploader = new FineUploaderS3({
      options: _.merge(
        _.merge(this.defaultOptions, {
          validation: {
            sizeLimit: this.sizeLimit,
            allowedExtensions: this.allowedExtensions,
            itemLimit: this.itemLimit
          }
        }),
        this.customOptions
      )
    })

    if (this.fineUploader && this.fineUploader.on) {
      this.fineUploader.on('totalProgress', this.onTotalProgress)
      this.fineUploader.on('progress', this.onProgress)
      this.fineUploader.on('submitted', this.onSubmitUpload)
      this.fineUploader.on('uploadChunk', this.onUploadChunk)
      this.fineUploader.on('complete', this.onUploadComplete)
      this.fineUploader.on('allComplete', this.onAllUploadComplete)

      if (this.validateRepeatedFiles) {
        this.fineUploader.on('validate', this.onValidateHandler)
      }
    } else {
      console.error('FineUploaderS3 não foi inicializado corretamente')
    }
  },
  methods: {
    onAllComplete() {
      this.started = false
      this.$emit('all-complete', this.thread)
    },
    onError(errorId, errorName, errorReason) {
      this.$toasted.error(errorReason)
      this.$emit('error', errorId, errorName, errorReason)
    },
    onValidateBatch(files) {
      return new Promise(resolve => {
        _.each(files, file => {
          this.totalSize += file.size
        })

        this.numFiles += files.length
        resolve(true)
      })
    },
    onStatusChange(fileId, oldStatus, newStatus) {
      if (newStatus === this.fineUploader.qq.status.REJECTED) {
        const fileSize = this.fineUploader.methods.getSize(fileId)
        this.totalSize = this.totalSize - fileSize
        this.numFiles -= 1
      } else if (newStatus === this.fineUploader.qq.status.UPLOAD_SUCCESSFUL) {
        this.numSentFiles += 1
      }

      this.$emit('status-change', fileId, oldStatus, newStatus)
    },
    onCancel(fileId) {
      return new Promise(resolve => {
        const fileSize = this.fineUploader.methods.getSize(fileId)

        this.totalSize = this.totalSize - fileSize
        this.numFiles = this.numFiles - 1
        this.started = false
        this.$emit('cancel', fileId)
        resolve(true)
      })
    },
    clearStoredFiles() {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.methods.clearStoredFiles()
      }
    },
    cancelAll() {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.methods.cancelAll()
        this.$emit('cancel-all', this.thread)
      }
    },
    cancel(fileId) {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.methods.cancel(fileId)
      }
    },
    pauseUpload(fileId) {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.methods.pauseUpload(fileId)
      }
    },
    pauseAll() {
      if (!_.isEmpty(this.fineUploader)) {
        const files = this.getVisibleUploads()
        _.each(files, file => {
          this.fineUploader.methods.pauseUpload(file.id)
        })
        this.paused = true
      }
    },
    continueUpload(fileId) {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.methods.continueUpload(fileId)
      }
    },
    continueAll() {
      if (!_.isEmpty(this.fineUploader)) {
        const files = this.getVisibleUploads()
        _.each(files, file => {
          this.fineUploader.methods.continueUpload(file.id)
        })

        this.paused = false
      }
    },
    retry(fileId) {
      if (!_.isEmpty(this.fineUploader)) {
        this.fineUploader.methods.retry(fileId)
      }
    },
    addFiles(inputResult, returnInfo) {
      this.fineUploader.methods.addFiles(inputResult)

      if (returnInfo) {
        const submittingFiles = this.fineUploader.methods.getUploads({
          status: [this.fineUploader.qq.status.SUBMITTING]
        })

        const filesInfos = []

        _.each(submittingFiles, file => {
          filesInfos.push(this.getFileInfo(file.id))
        })

        return Promise.all(filesInfos)
      }
    },
    onTotalProgress(bytesUploaded, totalSize) {
      this.bytesUploaded = bytesUploaded
      this.totalSize = totalSize

      // Calculate speed mbps
      this.bytesUploadedOverTime.push({
        totalUploadedBytes: bytesUploaded,
        currentTime: new Date().getTime()
      })

      const minSamples = 6
      const maxSamples = 20

      if (this.bytesUploadedOverTime.length > maxSamples) {
        this.bytesUploadedOverTime.shift()
      }
      if (this.bytesUploadedOverTime.length >= minSamples) {
        const firstSample = this.bytesUploadedOverTime[0]
        const lastSample = this.bytesUploadedOverTime[this.bytesUploadedOverTime.length - 1]
        const progressBytes = lastSample.totalUploadedBytes - firstSample.totalUploadedBytes
        const progressTimeMS = lastSample.currentTime - firstSample.currentTime
        this.mbps = progressBytes / (progressTimeMS / 1000) / (1000 * 1000)
      }

      // calculate estimated time to finish upload

      if (this.mbps === 0) {
        this.estimatedTime = 0
      } else {
        this.estimatedTime = ((totalSize || 1) - (bytesUploaded || 1)) / 1000 / 1000 / this.mbps / 60 / 60
      }

      this.$emit('total-progress', bytesUploaded, totalSize)
    },
    onProgress(fileId, name, bytesUploaded, totalSize) {
      this.$emit('progress', fileId, name, bytesUploaded, totalSize)
    },
    onUploadChunk(fileId, name, chunkData) {
      this.$emit('upload-chunk', fileId, name, chunkData)
    },
    onSubmitUpload() {
      const filesInCurrentUploadSession = this.getVisibleUploads()

      if (filesInCurrentUploadSession.length) {
        this.$emit('files-selected', this.fineUploader)
        if (this.autoStart) {
          this.$nextTick(() => {
            this.startUpload()
          })
        }
      }

      return true
    },
    startUpload(getKeyNameFunction) {
      if (getKeyNameFunction) {
        this.fineUploader.getKeyNameFunction = getKeyNameFunction
      }

      const files = this.fineUploader.methods.getUploads({ status: this.fineUploader.qq.status.SUBMITTED }) || []

      if (files.length > 0) {
        this.fineUploader.methods.uploadStoredFiles()
        this.started = true
      }
    },
    getInProgress() {
      return this.fineUploader.methods.getInProgress()
    },
    getUploads(payload) {
      return this.fineUploader.methods.getUploads(payload)
    },
    getVisibleUploads() {
      return this.fineUploader.methods.getUploads({
        status: [
          this.fineUploader.qq.status.SUBMITTING,
          this.fineUploader.qq.status.SUBMITTED,
          this.fineUploader.qq.status.QUEUED,
          this.fineUploader.qq.status.UPLOADING,
          this.fineUploader.qq.status.UPLOAD_RETRYING,
          this.fineUploader.qq.status.PAUSED
        ]
      })
    },
    keyNameFunction(fileId, fileName) {
      this.fileUuids[fileId] = this.fileUuids[fileId] || uuid()
      const fileSize = this.fineUploader.methods.getSize(fileId)
      const file = this.fineUploader.methods.getFile(fileId)
      const documentTypeId = file.documentTypeId
      let path = this.basePath
      if (fileName) {
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'))
        path = path.replace('{fileName}', fileName)
        path = path.replace('{fileExtension}', fileExtension)
      }

      path = path.replace('{documentTypeId}', documentTypeId)
      path = path.replace('{fileSize}', fileSize)
      path = path.replace('{fileId}', fileId)
      path = path.replace('{orgId}', this.$currentOrganization.id)
      path = path.replace('{uuid}', this.fileUuids[fileId])

      _.each(this.context, (item, key) => {
        path = path.replace('{' + key + '}', item)
      })

      return path
    },
    getFileInfo(fileId) {
      return new Promise(resolve => {
        this.fileUuids[fileId] = this.fileUuids[fileId] || uuid()
        const file = this.fineUploader.methods.getFile(fileId)
        const fileName = file.name
        const fileType = file.type || ''
        const fileKey = this.keyNameFunction(fileId, fileName)
        const fullPath = this.pathJoin([this.awsS3Url, fileKey])
        const fileSize = this.fineUploader.methods.getSize(fileId)
        const fileExtension = ((fileName && fileName.substring(fileName.lastIndexOf('.'))) || '').toLowerCase()
        const documentTypeId = file.documentTypeId
        const context = this.context
        const thread = this.thread
        const url =
          window.URL && window.URL.createObjectURL ? window.URL : window.webkitURL && window.webkitURL.createObjectURL ? window.webkitURL : null

        if ((this.imageUpload || IMAGE_EXTENSIONS.includes(fileExtension)) && url) {
          const image = new Image()

          image.onload = () => {
            resolve({
              uuid: this.fileUuids[fileId],
              context,
              thread,
              imageUpload: true,
              fileId,
              fileName,
              fileExtension,
              fileSize,
              documentTypeId,
              fileType,
              path: fileKey,
              fullPath,
              width: image.width,
              height: image.height,
              image,
              file
            })
          }

          image.src = url.createObjectURL(file)
        } else {
          resolve({
            uuid: this.fileUuids[fileId],
            context,
            thread,
            imageUpload: false,
            fileId,
            fileName,
            fileExtension,
            fileSize,
            documentTypeId,
            fileType,
            path: fileKey,
            fullPath,
            file
          })
        }
      })
    },
    onUploadComplete(fileId, fileName, reponseJson) {
      this.$emit('complete', fileId, fileName, reponseJson)

      if (reponseJson.success === false) return

      this.getFileInfo(fileId).then(fileInfo => {
        this.$emit('upload-complete', fileInfo)
      })
    },
    onAllUploadComplete(succeeded, failed) {
      this.$emit('all-upload-complete', { thread: this.thread, succeeded, failed })
    },
    onValidateHandler(file) {
      const filesInCurrentUploadSession = this.getVisibleUploads()

      const fileExistInCurrentUploadSession = _.findIndex(filesInCurrentUploadSession, selectedFile => {
        return selectedFile.name === file.name && selectedFile.size === file.size && selectedFile.status !== 'submitting'
      })

      if (fileExistInCurrentUploadSession !== -1) {
        return false
      }

      return true
    },
    isRetryForbidden(response) {
      const preventRetryResponseProperty =
        (this.fineUploader.options.retry && this.fineUploader.options.retry.preventRetryResponseProperty) || 'preventRetry'

      return !!response[preventRetryResponseProperty]
    }
  }
})
